import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
} from '@angular/core';

@Component({
  selector: 'fc-toggle',
  standalone: true,
  template: `
    <div class="flex items-center gap-12" [class.opacity-50]="disabled()">
      <label class="relative inline-block w-40 h-24 shrink-0">
        <input
          #input
          type="checkbox"
          class="opacity-0 w-0 h-0"
          [checked]="checked()"
          [disabled]="disabled()"
          (change)="onToggle($event)"
        />
        <span
          class="slider"
          [class.cursor-pointer]="!disabled()"
          [class.cursor-not-allowed]="disabled()"
        ></span>
      </label>

      <div
        class="text-base select-none flex items-center"
        [class.cursor-pointer]="!disabled()"
        [class.cursor-not-allowed]="disabled()"
        [class.text-grey-900]="!disabled()"
        [class.text-grey-500]="disabled()"
        (click)="!disabled() && input.click()"
      >
        @if (label()) {
          <div class="flex flex-col gap-2">
            <span>{{ label() }}</span>
            @if (description()) {
              <span class="text-sm text-grey-500">{{ description() }}</span>
            }
          </div>
        } @else {
          <ng-content />
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      display: inline-block;
    }

    .slider {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--color-secondary-50);
      border: 1.5px solid var(--color-secondary-500);
      transition: 0.2s;
      border-radius: 75px;
    }

    .slider:before {
      position: absolute;
      content: '';
      height: 18px;
      width: 18px;
      left: 2px;
      top: 1.5px;
      background-color: var(--color-secondary-500);
      transition: 0.2s;
      border-radius: 50%;
    }

    input:checked + .slider {
      background-color: var(--color-primary-600);
      border-color: transparent;
    }

    input:checked + .slider:before {
      background-color: var(--color-white);
      transform: translateX(17px);
    }

    input:focus-visible + .slider {
      box-shadow: 0 0 0 2px var(--color-primary-100);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleComponent {
  readonly checked = model(false);
  readonly label = input<string>();
  readonly description = input<string>();
  readonly disabled = input(false);

  readonly toggleChange = output<boolean>();

  onToggle(event: Event) {
    if (this.disabled()) {
      return;
    }
    const isChecked = (event.target as HTMLInputElement).checked;
    this.checked.set(isChecked);
    this.toggleChange.emit(isChecked);
  }
}
